/**
 * features/create-organization/ui/CreateOrganizationForm — Org creation form.
 *
 * Uses React Hook Form + Zod. On success:
 *   1. The new org becomes the active org via `useActiveOrg().setActiveOrg()`.
 *   2. Calls `onSuccess(org)` so the parent (dialog/route) can close/navigate.
 *
 * FSD: features may import from entities and shared.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { useCreateOrgMutation, useActiveOrg } from '@/entities/organization';
import type { Organization } from '@/entities/organization';
import { createOrganizationSchema, type CreateOrganizationFormValues } from '../model/schema';

interface CreateOrganizationFormProps {
  /** Called after successful creation. Receives the new org object. */
  onSuccess?: (org: Organization) => void;
  /** Called when the form encounters an error. Receives the localized message. */
  onError?: (message: string) => void;
}

export function CreateOrganizationForm({ onSuccess, onError }: CreateOrganizationFormProps) {
  const { t } = useTranslation('organizations');
  const createOrgMutation = useCreateOrgMutation();
  const { setActiveOrg } = useActiveOrg();

  const form = useForm<CreateOrganizationFormValues>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: { name: '' },
  });

  async function onSubmit(values: CreateOrganizationFormValues) {
    try {
      const newOrg = await createOrgMutation.mutateAsync({ name: values.name });
      // Make the new org active immediately.
      await setActiveOrg(newOrg);
      onSuccess?.(newOrg);
    } catch {
      const msg = t('create.error.generic');
      onError?.(msg);
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        className="space-y-4"
        data-testid="create-org-form"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('create.nameLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('create.namePlaceholder')}
                  autoComplete="organization"
                  data-testid="create-org-name-input"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="w-full"
          disabled={createOrgMutation.isPending}
          data-testid="create-org-submit"
        >
          {createOrgMutation.isPending ? t('create.submitting') : t('create.submit')}
        </Button>
      </form>
    </Form>
  );
}
